// MT-11B.7 — product strength vs prescribed dose (mirror of backend dose_compat.py).
// Field ownership: strength/concentration = product (master); dose + dose_unit =
// prescriber; admin_quantity only from an explicitly confirmed split.
const UNIT_CANON = {
  mg: "mg", mcg: "mcg", "µg": "mcg", ug: "mcg", g: "g", gm: "g", ml: "ml",
  iu: "IU", u: "U", unit: "U", units: "U", "%": "%",
  tab: "tab", tabs: "tab", tablet: "tab", tablets: "tab",
  cap: "cap", caps: "cap", capsule: "cap", capsules: "cap",
  puff: "puff", puffs: "puff", drop: "drop", drops: "drop",
};
const AMOUNT_RE = /^\s*(\d+(?:\.\d+)?)\s*([a-zA-Zµ%]+)?\s*(?:\/\s*(?:day|d|dose|week|wk|night))?\s*$/;
const CONC_RE = /^\s*u\s*-?\s*(40|100|200|300|500)\s*$/i;
const CONC_PER_ML_RE = /^\s*(40|100|200|300|500)\s*(?:iu|u|units?)\s*(?:\/|per)\s*ml\s*$/i;
const CONC_VALUES = new Set([40, 100, 200, 300, 500]);
const UNSUITABLE_RE = /\b(sr|xr|er|cr|pr|mr|xl|la|ec|dr|dsr|od|odt|retard|depot|enteric|gastro|modified|extended|prolonged|controlled|delayed|sustained|softgel|caps?|capsules?)\b/i;
const FRACTION_LABELS = { 0.25: "¼ tablet", 0.5: "½ tablet", 0.75: "¾ tablet", 1.5: "1½ tablets", 2.5: "2½ tablets", 3.5: "3½ tablets" };
const COMBINED_RE = /[A-Za-z)]\s+\/\s+[A-Za-z(]|\s+or\s+|\(or\b/i; // spaced " / " or " or " only
const INSULIN_RE = /insulin|glargine|degludec|detemir|aspart|lispro|glulisine|isophane|\bnph\b|mixtard|novomix|humalog|lantus|basalog|toujeo|tresiba|ryzodeg|novorapid|fiasp|actrapid|humulin|huminsulin|insugen|wosulin|basaglar|semglee/i;

export function parseAmount(text) {
  const s = String(text ?? "").replace(/,/g, "").trim();
  const m = AMOUNT_RE.exec(s);
  if (!m) return { value: null, unit: null };
  const raw = (m[2] || "").toLowerCase();
  if (raw && !(raw in UNIT_CANON)) return { value: null, unit: null };
  return { value: parseFloat(m[1]), unit: raw ? UNIT_CANON[raw] : null };
}

export function insulinConcentration(text) {
  const s = String(text ?? "").trim();
  if (!s) return null;
  const m = CONC_RE.exec(s) || CONC_PER_ML_RE.exec(s);
  if (m) return `U-${parseInt(m[1], 10)}`;
  const { value, unit } = parseAmount(s);
  if (value != null && (unit === "U" || unit === "IU") && Number.isInteger(value) && CONC_VALUES.has(value)) return `U-${value}`;
  return null;
}

export const isInsulinRow = (row) => INSULIN_RE.test(`${row?.generic_name || ""} ${row?.brand_name || ""} ${row?.composition || ""}`);

export function isUnitDoseAsStrength(strength, row) {
  const s = String(strength ?? "");
  if (insulinConcentration(s)) return false;
  const { value, unit } = parseAmount(s);
  if (value == null || !(unit === "U" || unit === "IU")) return false;
  const perDay = /\/\s*(day|d|dose|night)\b/i.test(s);
  return perDay || (row ? isInsulinRow(row) : false);
}

export const isCombinedProductName = (brand) => { const b = String(brand ?? "").trim(); return !!b && COMBINED_RE.test(b); };

const tabletLike = (form) => /\btab/i.test(form || "") || !String(form || "").trim();

export function inheritDoseUnit(dose, strength, prevUnit, dosageForm) {
  const d = String(dose ?? "").trim();
  const { value, unit } = parseAmount(d);
  if (value == null || unit) return d;
  if (prevUnit && Object.values(UNIT_CANON).includes(prevUnit)) return `${d} ${prevUnit}`;
  const s = parseAmount(strength);
  if (s.value && ["mg", "mcg", "g", "ml", "U", "IU"].includes(s.unit) && [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4].includes(value / s.value)) return `${d} ${s.unit}`;
  const form = String(dosageForm || "");
  if (value <= 4 && Number.isInteger(value * 2) && (tabletLike(form) || /\bcap/i.test(form))) return `${d} ${/cap/i.test(form) ? "cap" : "tab"}`;
  return d;
}

export function splitFraction(dose, strength, dosageForm, brandName) {
  const form = String(dosageForm || "");
  const dv = parseAmount(dose), sv = parseAmount(strength);
  if (!dv.value || !sv.value || dv.unit !== sv.unit || !["mg", "mcg", "g"].includes(dv.unit)) return null;
  if (!tabletLike(form) && !/\bcap/i.test(form)) return null;
  const ratio = Math.round((dv.value / sv.value) * 10000) / 10000;
  if (Number.isInteger(ratio)) return null;
  const label = FRACTION_LABELS[ratio];
  const unsuitable = UNSUITABLE_RE.exec(`${brandName || ""} ${form} ${strength || ""}`);
  if (!label) return { fraction: ratio, label: `${ratio} tablet`, suitable: false, reason: "dose is not a whole, half or quarter of this tablet — choose a matching product" };
  if (unsuitable) return { fraction: ratio, label, suitable: false, reason: `'${unsuitable[1]}' products must not be split — choose a matching product or dose` };
  return { fraction: ratio, label, suitable: true, reason: "" };
}

/** Normalize ONE row; returns { row, issues } — issues are blocking. */
export function normalizeMedicine(m) {
  const out = { ...m };
  const issues = [];
  const brand = String(out.brand_name || "").trim();
  let strength = String(out.strength || "").trim();
  let dose = String(out.dose || "").trim();
  const form = String(out.dosage_form || "");
  const insulin = isInsulinRow(out);
  if (isUnitDoseAsStrength(strength, out)) {
    if (!dose) dose = strength;
    strength = insulinConcentration(out.concentration) || "";
    out.strength = strength;
  }
  const conc = insulinConcentration(out.concentration) || (insulin ? insulinConcentration(strength) : null);
  if (conc) out.concentration = conc;
  dose = inheritDoseUnit(dose, strength, out.dose_unit, form);
  out.dose = dose;
  const { value, unit } = parseAmount(dose);
  if (unit) out.dose_unit = unit;
  else if (value != null) issues.push(`${brand || "Medicine"}: dose '${dose}' needs a unit (e.g. 50 mg, 12 U, 1 tab)`);
  const frac = splitFraction(dose, strength, form, brand);
  if (!frac) { delete out.admin_quantity; delete out.split_confirmed; }
  else if (!frac.suitable) issues.push(`${brand}: ${dose} is ${frac.label} of a ${strength} ${form.trim() || "tablet"} — ${frac.reason}`);
  else if (!out.split_confirmed) issues.push(`${brand}: ${dose} is ${frac.label} of a ${strength} tablet — confirm tablet splitting or choose a matching product`);
  else { out.split_confirmed = true; out.admin_quantity = frac.label; }
  if (isCombinedProductName(brand)) out.product_unverified = true; else delete out.product_unverified;
  return { row: out, issues };
}

export function normalizeRxRows(rows) {
  const out = [], issues = [];
  for (const r of rows || []) { const n = normalizeMedicine(r); out.push(n.row); issues.push(...n.issues); }
  return { rows: out, issues };
}

/** Printable dose: "½ tablet (50 mg) of 100 mg tablet" for a confirmed split. */
export function doseDisplay(m) {
  const dose = String(m?.dose || "");
  if (m?.admin_quantity && m?.strength && m?.split_confirmed) return `${m.admin_quantity} (${dose}) of ${m.strength} tablet`;
  return dose;
}

/** Strength shown next to a product name only when it is a product strength. */
export const productStrengthLabel = (row) => (row?.strength && !isUnitDoseAsStrength(row.strength, row) ? row.strength : "");

// ===================================================== MT-11B.8 data-quality guard
// Mirror of backend dose_compat.assess_quality. RED blocks Save & Print; AMBER
// needs the prescriber's explicit confirmation (recorded on the row).
export const QUALITY_VERSION = "mt11b8";
export const CONFIRMABLE_CODES = ["tablet_split", "dose_in_title", "unverified_product", "strength_dose_mismatch"];
export const CORRECTION_FIELDS = ["brand_name", "generic_name", "strength", "concentration", "dosage_form", "dose", "dose_unit", "route"];
const KNOWN_PRODUCTS = {
  lantus: { route: "SC", form: "Injection", concentration: "U-100" }, basalog: { route: "SC", form: "Injection", concentration: "U-100" },
  basaglar: { route: "SC", form: "Injection", concentration: "U-100" }, semglee: { route: "SC", form: "Injection", concentration: "U-100" },
  glaritus: { route: "SC", form: "Injection", concentration: "U-100" }, toujeo: { route: "SC", form: "Injection", concentration: "U-300" },
  tresiba: { route: "SC", form: "Injection", concentration: "U-100" }, ryzodeg: { route: "SC", form: "Injection", concentration: "U-100" },
  levemir: { route: "SC", form: "Injection", concentration: "U-100" }, novorapid: { route: "SC", form: "Injection", concentration: "U-100" },
  fiasp: { route: "SC", form: "Injection", concentration: "U-100" }, apidra: { route: "SC", form: "Injection", concentration: "U-100" },
  novomix: { route: "SC", form: "Injection", concentration: "U-100" }, "humalog 200": { route: "SC", form: "Injection", concentration: "U-200" },
  humalog: { route: "SC", form: "Injection", concentration: "U-100" },
  // MT-11B.8.1 member regimen facts: freq daily|weekly, doses_mg marketed strengths, forbid = another member's regimen text
  ozempic: { route: "SC", form: "Injection", freq: "weekly", doses_mg: [0.25, 0.5, 1, 2], forbid: /\boral\b|\btablet|\b(3|7|14)\s*mg\b|30\s*min/i },
  rybelsus: { route: "Oral", form: "Tablet", freq: "daily", doses_mg: [3, 7, 14], forbid: /\binject|\bpen\b|weekly|\b0\.25\s*mg|\b0\.5\s*mg|\b1(\.0)?\s*mg\b|\bsc\b/i },
  trulicity: { route: "SC", form: "Injection", freq: "weekly" }, victoza: { route: "SC", form: "Injection", freq: "daily" },
  mounjaro: { route: "SC", form: "Injection", freq: "weekly" },
};
export const REGIMEN_FIELDS = ["dose", "dose_unit", "frequency", "timing", "duration", "instructions", "quantity", "admin_quantity", "split_confirmed", "standard_dose", "titration", "notes"];
const WEEKLY_RE = /week|q7d|\bqw\b/i;
const DAILY_RE = /\b(od|bd|tds|tid|qid|hs|daily|once a day|twice|thrice|morning|night|bedtime)\b/i;
const TITRATION_RE = /→|->|\(start\)|\bthen\b|\bx\s*\d+\s*wk/i;

export function knownProduct(brand) {
  const low = String(brand ?? "").trim().toLowerCase();
  for (const k of Object.keys(KNOWN_PRODUCTS).sort((a, b) => b.length - a.length)) if (low.startsWith(k) || ` ${low}`.includes(` ${k}`)) return { key: k, ...KNOWN_PRODUCTS[k] };
  return null;
}

/** MT-11B.8.1 — a resolved/known product may not carry another member's regimen (mirror of backend coherence_issues). */
export function coherenceIssues(m) {
  const red = [];
  const brand = String(m.brand_name || "").trim();
  if (!brand || isCombinedProductName(brand)) return red;
  const dose = String(m.dose || ""), freq = String(m.frequency || ""), instr = String(m.instructions || ""), strength = String(m.strength || "");
  const group = String(m.search_group || "");
  if (TITRATION_RE.test(dose)) red.push({ code: "titration_in_dose", message: `${brand}: dose '${dose}' is a schedule, not one dose — enter a single dose and put the titration in instructions` });
  if (group) {
    if (!dose.trim() || !freq.trim()) red.push({ code: "resolved_row_incomplete", message: `${brand} was chosen from '${group}' — enter the prescribed dose and frequency for this product (group values are never carried over)` });
    const blob = [instr, freq, dose, strength, String(m.timing || "")].join(" ").toLowerCase();
    for (const n of groupMembers(group)) {
      const core = n.replace(/\s*\(.*$/, "").trim().toLowerCase();
      if (core.length >= 4 && !brand.toLowerCase().includes(core) && new RegExp(`\\b${core.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(blob)) red.push({ code: "group_member_contamination", message: `${brand}: regimen text still refers to '${n}' from the search group` });
    }
    const gr = m.correction?.group_regimen;
    if (gr && String(gr.instructions || "").trim() && String(instr).trim() === String(gr.instructions).trim()) red.push({ code: "group_regimen_retained", message: `${brand}: administration instructions were inherited from the unresolved group — re-enter them for this product` });
  }
  const kp = knownProduct(brand);
  if (kp) {
    if (kp.concentration) { const rc = insulinConcentration(m.concentration) || insulinConcentration(strength); if (rc && rc !== kp.concentration) red.push({ code: "member_concentration_mismatch", message: `${brand} is ${kp.concentration} — row carries ${rc}` }); }
    if (kp.freq === "daily" && WEEKLY_RE.test(freq)) red.push({ code: "member_frequency_conflict", message: `${brand} is taken daily — frequency '${freq}' belongs to a weekly product` });
    if (kp.freq === "weekly" && DAILY_RE.test(freq) && !WEEKLY_RE.test(freq)) red.push({ code: "member_frequency_conflict", message: `${brand} is a once-weekly product — frequency '${freq}' is a daily schedule` });
    if (kp.doses_mg) { const { value: dv, unit: du } = parseAmount(dose); if (dv != null && du === "mg" && !kp.doses_mg.some((x) => Math.abs(dv - x) < 1e-9)) red.push({ code: "member_dose_out_of_range", message: `${brand}: ${dose} is not a ${brand.split(" ")[0]} dose (${kp.doses_mg.join("/")} mg)` }); }
    if (kp.forbid && kp.forbid.test([instr, freq, dose].join(" "))) red.push({ code: "member_regimen_contamination", message: `${brand}: instructions/frequency/dose contain another product's regimen (check for injectable vs oral schedule)` });
  }
  return red;
}

/**
 * MT-11B.8.1 — rebuild a row from the selected member: identity/product facts from the
 * member (master hit or built-in profile), EVERY regimen field cleared, group snapshot kept.
 */
export function buildResolvedRow(row, memberName, hit) {
  const groupRow = row.correction?.group_row || row;                      // always rebuild from the ORIGINAL group row
  const group = row.search_group || (isCombinedProductName(row.brand_name) ? row.brand_name : "");
  const snap = row.correction?.original ? row : beginCorrection(groupRow);
  const keep = {};
  for (const k of ["_uid", "_prov", "medicine_origin", "origin_detail", "recommendation_id", "guidance_session_id", "type", "insulin_regimen"]) if (row[k] !== undefined) keep[k] = row[k];
  const prof = memberProfile(memberName, hit);
  const cleanName = memberName.replace(/\s*\((?:oral|tab(?:let)?s?|inj(?:ection)?)\)\s*/i, "").trim();
  const groupGeneric = isCombinedProductName(groupRow.generic_name) ? "" : (groupRow.generic_name || "");
  const groupRegimen = {}; for (const k of ["dose", "frequency", "timing", "duration", "instructions", "standard_dose"]) groupRegimen[k] = groupRow[k] ?? "";
  const suggestion = {};
  if (hit?.standard_dose && parseAmount(hit.standard_dose).value != null && !TITRATION_RE.test(hit.standard_dose)) suggestion.dose = hit.standard_dose;
  if (hit?.default_frequency) suggestion.frequency = hit.default_frequency;
  return {
    ...keep,
    brand_name: hit?.brand_name || cleanName, generic_name: hit?.generic_name || groupGeneric, composition: hit?.composition || "",
    strength: hit?.strength || prof.strength || "", concentration: hit?.concentration || prof.concentration || "",
    dosage_form: hit?.dosage_form || prof.form || "", route: hit?.route || prof.route || "",
    medicine_id: hit?.medicine_id || null, selected_from_master: !!hit, medicine_source: hit ? "mylist" : undefined,
    dose: "", dose_unit: "", frequency: "", timing: "", duration: "", instructions: "", quantity: "",
    admin_quantity: undefined, split_confirmed: false, standard_dose: undefined, product_unverified: false,
    quality_confirmations: [], search_group: group,
    member_suggestion: Object.keys(suggestion).length ? suggestion : undefined,
    correction: { ...snap.correction, source: "search_group", group_regimen: snap.correction?.group_regimen || groupRegimen, group_row: snap.correction?.group_row || { ...groupRow, correction: undefined } },
  };
}
const HINT_ORAL_RE = /\((?:oral|tab(?:let)?s?)\)|\btab(?:let)?s?\b|\bcap(?:sule)?s?\b/i;
const HINT_INJ_RE = /\binj(?:ection)?\b|\bpen\b|solostar|flexpen|kwikpen|cartridge|penfill|\bsc\b/i;
const DOSE_IN_TITLE_RE = /\d+(?:\.\d+)?\s*(?:u|iu|units?)\b(?!\s*\/\s*ml)|\/\s*day\b|\(start\)|→|->/i;
const LIQUID_FORM_RE = /syrup|suspension|solution|drops?|liquid|elixir|injection|inj\b|pen|cartridge|vial|ampoule|nebul|inhaler|spray/i;

export function groupMembers(brand) {
  let b = String(brand ?? "").trim();
  if (!isCombinedProductName(b)) return [];
  b = b.replace(/\(\s*or\s+/gi, "/ ");
  const out = [];
  for (let p of b.split(/\s+\/\s+|\s+or\s+/i)) {
    p = p.replace(/\s+/g, " ").replace(/^[\s-]+|[\s-]+$/g, "");
    while ((p.match(/\)/g) || []).length > (p.match(/\(/g) || []).length) p = p.replace(/\)\s*$/, "");
    if (p && !out.some((x) => x.toLowerCase() === p.toLowerCase())) out.push(p);
  }
  return out;
}

export function memberProfile(name, master) {
  const key = String(name ?? "").trim(), low = key.toLowerCase();
  const prof = { name: key, route: "", form: "", concentration: "", strength: "", medicine_id: null, source: "text" };
  if (master) {
    Object.assign(prof, {
      route: String(master.route || ""), form: String(master.dosage_form || ""), strength: String(master.strength || ""),
      concentration: insulinConcentration(master.concentration) || (isInsulinRow(master) ? insulinConcentration(master.strength) : null) || "",
      medicine_id: master.medicine_id || null, source: "master",
    });
  }
  for (const k of Object.keys(KNOWN_PRODUCTS).sort((a, b) => b.length - a.length)) {
    if (low.startsWith(k) || ` ${low}`.includes(` ${k}`)) { for (const [f, v] of Object.entries(KNOWN_PRODUCTS[k])) prof[f] = prof[f] || v; break; }
  }
  if (!prof.route) prof.route = HINT_ORAL_RE.test(key) ? "Oral" : HINT_INJ_RE.test(key) ? "SC" : "";
  if (!prof.form) prof.form = HINT_ORAL_RE.test(key) ? "Tablet" : HINT_INJ_RE.test(key) ? "Injection" : "";
  if (!prof.concentration && isInsulinRow({ brand_name: key })) prof.concentration = insulinConcentration(key) || "";
  return prof;
}

const liquidOrInjectable = (m) => LIQUID_FORM_RE.test(String(m.dosage_form || "")) || ["SC", "IV", "IM", "INHALATION"].includes(String(m.route || "").toUpperCase()) || isInsulinRow(m);

/** Classify ONE normalized row → { red: [{code,message}], amber: [{code,message,fix_only?}] } */
export function assessQuality(m, members) {
  const red = [], amber = [];
  const brand = String(m.brand_name || "").trim(), generic = String(m.generic_name || "").trim();
  const strength = String(m.strength || "").trim(), dose = String(m.dose || "").trim();
  const route = String(m.route || "").trim(), form = String(m.dosage_form || "").trim();
  const label = brand || generic || "Medicine";
  if (!brand && !generic) red.push({ code: "missing_identity", message: "Medicine has no product or generic name" });
  if (!route) red.push({ code: "missing_route", message: `${label}: route is required` });
  if (isCombinedProductName(brand)) {
    const names = groupMembers(brand);
    const profs = members || names.map((n) => memberProfile(n));
    red.push({ code: "search_group_unresolved", message: `'${brand}' is a search group of ${names.length || "several"} products — select ONE definite product or specify it manually` });
    const routes = new Set(profs.filter((p) => p.route).map((p) => p.route));
    if (routes.size > 1) red.push({ code: "route_conflict", message: `${brand}: members have different routes/forms (${[...routes].sort().join(", ")})` });
    const ins = profs.filter((p) => isInsulinRow({ brand_name: p.name }));
    if (ins.length > 1) {
      const concs = new Set(ins.map((p) => p.concentration || "unknown"));
      if (concs.size > 1 || concs.has("unknown")) red.push({ code: "insulin_concentration_conflict", message: `${brand}: insulin products with different or unknown concentrations (${[...concs].sort().join(", ")})` });
    }
    const strengths = new Set(profs.filter((p) => p.strength && parseAmount(p.strength).value != null).map((p) => p.strength));
    if (strengths.size > 1) red.push({ code: "strength_conflict", message: `${brand}: members carry different strengths (${[...strengths].sort().join(", ")})` });
  } else {
    const prof = brand ? memberProfile(brand) : {};
    if (prof.route && route && prof.route.toLowerCase() !== route.toLowerCase()) red.push({ code: "route_conflict", message: `${brand} is given ${prof.route}${prof.form ? ` (${prof.form})` : ""} — route '${route}' conflicts` });
    if (brand && !m.medicine_id && !m.selected_from_master) amber.push({ code: "unverified_product", message: `${label}: free-text product not selected from My List / Old DB — confirm it is one definite product` });
  }
  const { value: dv, unit: du } = parseAmount(dose);
  if (dv != null && !du) {
    if (liquidOrInjectable(m)) red.push({ code: "missing_dose_unit", message: `${label}: dose '${dose}' needs a unit (U, ml, mg…) — clinically necessary for this formulation` });
    else amber.push({ code: "unitless_dose", fix_only: true, message: `${label}: dose '${dose}' has no unit — enter mg / mcg / tab in 'Correct for this prescription'` });
  }
  const titleScan = isInsulinRow(m) ? brand.replace(/\b(40|100|200|300|500)\s*(?:iu|u|units?)\b/gi, " ") : brand; // concentration labels are product facts
  const strengthScan = insulinConcentration(strength) ? "" : strength;
  if (DOSE_IN_TITLE_RE.test(titleScan) || DOSE_IN_TITLE_RE.test(strengthScan) || strength.toLowerCase().includes("(start)")) amber.push({ code: "dose_in_title", message: `${label}: a dose / titration is embedded in the product title or strength — confirm the prescribed dose field is the authoritative dose` });
  const { unit: su } = parseAmount(strength);
  if (["mg", "mcg", "g"].includes(du) && ["mg", "mcg", "g"].includes(su) && du !== su) amber.push({ code: "strength_dose_mismatch", message: `${label}: dose unit ${du} differs from product strength ${strength} — confirm` });
  const frac = splitFraction(dose, strength, form, brand);
  if (frac) {
    if (!frac.suitable) red.push({ code: "impractical_fraction", message: `${label}: ${dose} is ${frac.label} of a ${strength} ${form || "tablet"} — ${frac.reason}` });
    else amber.push({ code: "tablet_split", message: `${label}: ${dose} is ${frac.label} of a ${strength} tablet — confirm tablet splitting` });
  }
  red.push(...coherenceIssues(m)); // MT-11B.8.1
  return { red, amber };
}

export const confirmedCodes = (m) => { const s = new Set((m.quality_confirmations || []).map(String)); if (m.split_confirmed) s.add("tablet_split"); return s; };
export const unresolvedAmber = (m, amber) => { const c = confirmedCodes(m); return amber.filter((a) => a.fix_only || !c.has(a.code)); };

/** Full per-row verdict used by MedicineRow + Save & Print gate. */
export function rowQuality(row) {
  const { row: n } = normalizeMedicine(row);
  const q = assessQuality(n);
  return { normalized: n, red: q.red, amber: q.amber, pending: unresolvedAmber(n, q.amber), blocked: q.red.length > 0 || unresolvedAmber(n, q.amber).length > 0 };
}

/** Snapshot the DB-derived values once before the doctor edits them (per-Rx correction record). */
export function beginCorrection(row) {
  if (row.correction?.original) return row;
  const original = {};
  for (const k of CORRECTION_FIELDS) original[k] = row[k] ?? "";
  return { ...row, correction: { original, source: row.medicine_id ? (row.medicine_source || "mylist") : (row.search_group || isCombinedProductName(row.brand_name) ? "search_group" : "free_text"), source_id: row.medicine_id || null } };
}

/** Printed handouts must never show literal Markdown markers. */
export function stripMarkdown(text) {
  let s = String(text ?? "");
  if (!s) return "";
  s = s.replace(/```[a-zA-Z]*\n?|```/g, "");
  s = s.replace(/(\*\*|__)([\s\S]+?)\1/g, "$2");
  s = s.replace(/(^|[^\w*])\*(?!\s)([^*\n]+?)\*(?![\w*])/g, "$1$2");
  s = s.replace(/(^|[^\w])_(?!\s)([^_\n]+?)_(?!\w)/g, "$1$2");
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  s = s.replace(/^(\s*)[*•]\s+/gm, "$1- ");
  s = s.replace(/`([^`\n]*)`/g, "$1");
  return s.replace(/\*\*/g, "");
}
